import { useMemo, useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { ROLES } from "../constants/roles";
import { useNavigate } from "react-router-dom";
import InstructorDashboard from "./InstructorDashboard";
import AdminDashboard from "./AdminDashboard";
import "./Dashboard.css";
import { supabase } from "../lib/supabase";

const studentMenuItems = [
  { key: "home", label: "Overview", icon: "OV" },
  { key: "courses", label: "My Courses", icon: "CR" },
  { key: "live", label: "Live Classes", icon: "LV" },
  { key: "recordings", label: "Recordings", icon: "RC" },
  { key: "modules", label: "Modules", icon: "MD" },
  { key: "assignments", label: "Assignments", icon: "AS" },
  { key: "projects", label: "Projects", icon: "PJ" },
  { key: "results", label: "Results", icon: "RS" },
  { key: "career", label: "Job Assistance", icon: "JA" },
  { key: "profile", label: "Profile", icon: "PF" },
];

const studentMetrics = [
  {
    label: "Course Progress",
    value: "35%",
    note: "Overall completion",
    type: "progress",
  },
  {
    label: "Modules Completed",
    value: "3 / 10",
    note: "7 modules remaining",
  },
  {
    label: "Pending Assignments",
    value: "2",
    note: "Check upcoming deadlines",
  },
  {
    label: "Projects Completed",
    value: "1 / 5",
    note: "Build your portfolio",
  },
];


const announcements = [
  {
    title: "New course cohort opens this week",
    description: "Applications for the next full stack cohort are now live.",
    tag: "Program update",
  },
  {
    title: "Assignment deadline updated",
    description: "Frontend capstone submissions now close on Friday at 8 PM.",
    tag: "Deadline",
  },
];

const upcomingTasks = [
  { title: "Submit React dashboard assignment", due: "Due tomorrow" },
  { title: "Review JavaScript async quiz", due: "Due in 2 days" },
];

function StudentDashboard({ userData }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [active, setActive] = useState("home");
  const [openDropdown, setOpenDropdown] = useState(false);
  const [liveClasses, setLiveClasses] = useState([]);
  const [myCourses, setMyCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(true);

  const getNameFromEmail = (email = "") => {
  if (!email) return "Student";

  const username = email.split("@")[0];

  return username
    .replace(/[0-9]/g, "")
    .replace(/[._-]/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim();
};

const displayName =
  userData?.name ||
  user?.user_metadata?.full_name ||
  user?.user_metadata?.name ||
  getNameFromEmail(user?.email);
  const profilePhoto = userData?.photoURL || user?.photoURL || userData?.picture || user?.picture || "";
  const hasPhoto = Boolean(profilePhoto);
  const initials = displayName
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

useEffect(() => {

  fetchLiveClasses();
  const fetchMyCourses = async () => {

  if (!user?.email) return;
  console.log("Logged In User:", user);
  console.log("Logged In Email:", user?.email);

  setLoadingCourses(true);

  // Find this user's enrollment
  const { data: enrollments, error: enrollError } =
    await supabase
      .from("enrollments")
      .select("course_slug")
      .eq("email", user.email);

  console.log("Enrollments:", enrollments);
  console.log("Enrollment Error:", enrollError);

  if (enrollError) {
    console.log(enrollError);
    setLoadingCourses(false);
    return;
  }

  if (!enrollments || enrollments.length === 0) {
    setMyCourses([]);
    setLoadingCourses(false);
    return;
  }

  const slugs = enrollments.map(item => item.course_slug);
  console.log("Slugs:", slugs);

  const { data: courses, error: courseError } =
    await supabase
      .from("courses")
      .select("*")
      .in("slug", slugs);
  console.log("Courses:", courses);
  console.log("Course Error:", courseError);

  if (courseError) {
    console.log(courseError);
    setLoadingCourses(false);
    return;
  }

  setMyCourses(courses || []);
  setLoadingCourses(false);

};
  fetchMyCourses();

}, []);

const fetchLiveClasses = async () => {

  const { data, error } =
    await supabase
      .from("live_classes")
      .select("*")
      .order("created_at", {
        ascending: false,
      });

  if (!error && data) {
    setLiveClasses(data);
  }
};
const nextLiveClass = liveClasses?.[0] || null;

  const pageTitle = useMemo(
    () => studentMenuItems.find((item) => item.key === active)?.label || "Overview",
    [active]
  );

  const renderOverview = () => (
    <>
      <section className="sd-hero">

  <div className="sd-welcome-card">

    <span className="sd-kicker">
      Student Workspace
    </span>

    <span className="sd-course-badge">
      Frontend Development Program
    </span>

    <h1>
      Welcome back, {displayName.split(" ")[0] || "Student"} 👋
    </h1>

    <p>
      Continue your learning journey, stay on top of your
      assignments, and keep building your development skills.
    </p>

    <div className="sd-hero-actions">

      <button
        className="sd-primary-btn"
        onClick={() => setActive("courses")}
      >
        Continue Learning
      </button>

      <button
        className="sd-secondary-btn"
        onClick={() => setActive("modules")}
      >
        View Modules
      </button>

    </div>

  </div>


  <div className="sd-next-class-card">

    <span className="sd-hero-label">
      Next Live Class
    </span>

    {nextLiveClass ? (
      <>
        <strong>
          {nextLiveClass.class_title}
        </strong>

        <p>
          {nextLiveClass.course}
        </p>

        <div className="sd-class-info">

          <span>
            Mentor
          </span>

          <strong>
            {nextLiveClass.mentor_name}
          </strong>

        </div>

        {nextLiveClass.class_link && (
          <a
            href={nextLiveClass.class_link}
            target="_blank"
            rel="noreferrer"
            className="sd-primary-btn sd-class-join-btn"
          >
            Join Class
          </a>
        )}

      </>
    ) : (
      <>
        <strong>
          No upcoming class
        </strong>

        <p>
          Your mentor hasn't published the next session yet.
        </p>
      </>
    )}

  </div>

</section>

      <section className="sd-metric-grid">

  {studentMetrics.map((metric) => (

    <article
      key={metric.label}
      className="sd-metric-card"
    >

      <div className="sd-metric-top">

        <span>
          {metric.label}
        </span>

        <span className="sd-metric-indicator">
          ↗
        </span>

      </div>

      <strong>
        {metric.value}
      </strong>

      {metric.type === "progress" && (
        <div className="sd-progress-track">

          <div
            className="sd-progress-fill"
            style={{ width: "35%" }}
          />

        </div>
      )}

      <p>
        {metric.note}
      </p>

    </article>

  ))}

</section>

      <section className="sd-content-grid">
        <div className="sd-panel sd-panel-large">

  <div className="sd-panel-head">

    <div>
      <span className="sd-panel-kicker">
        YOUR LEARNING
      </span>

      <h3>
        Continue Learning
      </h3>
    </div>

    <button
      className="sd-text-btn"
      onClick={() => setActive("courses")}
    >
      View all
    </button>

  </div>


  {loadingCourses ? (

    <div className="sd-placeholder">
      <strong>Loading your courses...</strong>
    </div>

  ) : myCourses.length === 0 ? (

    <div className="sd-placeholder">

      <strong>
        No course assigned yet
      </strong>

      <p>
        Your enrolled course will appear here once it is assigned.
      </p>

    </div>

  ) : (

    <div className="sd-learning-list">

      {myCourses.slice(0, 2).map((course) => (

        <div
          key={course.id}
          className="sd-learning-card"
        >

          <div className="sd-learning-main">

            <span className="sd-learning-tag">
              ACTIVE PROGRAM
            </span>

            <h4>
              {course.title}
            </h4>

            <p>
              {course.description}
            </p>

          </div>

          <button
            className="sd-primary-btn"
            onClick={() => setActive("courses")}
          >
            Continue
          </button>

        </div>

      ))}

    </div>

  )}

</div>

        <div className="sd-panel">
          <div className="sd-panel-head">
            <h3>Upcoming tasks</h3>
            <span>Stay on schedule</span>
          </div>
          <div className="sd-task-list">
            {upcomingTasks.map((task, index) => (

  <div
    key={task.title}
    className="sd-task-item"
  >

    <div className="sd-task-number">
      {String(index + 1).padStart(2, "0")}
    </div>

    <div>

      <strong>
        {task.title}
      </strong>

      <span>
        {task.due}
      </span>

    </div>

  </div>

))}
          </div>
        </div>
      </section>

      <section className="sd-panel">

  <div className="sd-panel-head">
    <h3>Live Classes</h3>
    <span>Join mentor sessions</span>
  </div>

  {liveClasses.length === 0 ? (

    <div className="sd-placeholder">
      <strong>No live classes</strong>

      <p>
        Mentors have not published
        any live sessions yet.
      </p>
    </div>

  ) : (

    <div className="sd-announcement-list">

      {liveClasses.map((item) => (

        <div
          key={item.id}
          className="sd-announcement-card"
        >

          <div>

            <strong>
              {item.class_title}
            </strong>

            <p>
              Course: {item.course}
            </p>

            <p>
              Mentor: {item.mentor_name}
            </p>

          </div>

          <a
            href={item.class_link}
            target="_blank"
            rel="noreferrer"
            className="sd-primary-btn"
            style={{
              textDecoration: "none",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            Join Class
          </a>

        </div>
      ))}
    </div>
  )}
</section>

      <section className="sd-panel">
        <div className="sd-panel-head">
          <h3>Announcements</h3>
          <span>Latest platform updates</span>
        </div>
        <div className="sd-announcement-list">
          {announcements.map((announcement) => (
            <div key={announcement.title} className="sd-announcement-card">
              <div>
                <strong>{announcement.title}</strong>
                <p>{announcement.description}</p>
              </div>
              <span>{announcement.tag}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="sd-career-section">

  <div className="sd-career-card">

    <div>
      <span className="sd-panel-kicker">
        CAREER SUPPORT
      </span>

      <h3>
        Job Assistance
      </h3>

      <p>
        Get support to strengthen your profile,
        prepare for interviews, and approach job
        opportunities with confidence.
      </p>
    </div>


    <div className="sd-career-options">

      <span>Resume & ATS Review</span>

      <span>LinkedIn Optimization</span>

      <span>Mock Interviews</span>

      <span>Portfolio & GitHub Review</span>

      <span>Interview Preparation</span>

      <span>Career Guidance</span>

    </div>


    <button
      className="sd-primary-btn"
      onClick={() => setActive("career")}
    >
      Explore Career Support
    </button>

  </div>


  <p className="sd-career-note">
    CODEION provides job assistance, career guidance,
    and interview preparation. Employment or placement
    is not guaranteed.
  </p>

</section>

    </>
  );

  const renderPlaceholder = (title, description) => (
    <section className="sd-panel">
      <div className="sd-panel-head">
        <h3>{title}</h3>
        <span>Coming next</span>
      </div>
      <div className="sd-placeholder">
        <strong>{title} area</strong>
        <p>{description}</p>
      </div>
    </section>
  );
const renderCourses = () => (
  <section className="sd-panel">

    <div className="sd-panel-head">
      <h3>My Courses</h3>
      <span>Your enrolled courses</span>
    </div>

    {loadingCourses ? (

      <div className="sd-placeholder">
        <strong>Loading...</strong>
      </div>

    ) : myCourses.length === 0 ? (

      <div className="sd-placeholder">
        <strong>No Course Assigned</strong>
        <p>Your mentor has not assigned any course yet.</p>
      </div>

    ) : (

      <div className="sd-course-grid">

        {myCourses.map(course => (

          <div
            key={course.id}
            className="sd-course-card"
          >

            <img
              src={`/courses/${course.thumbnail}`}
              alt={course.title}
              className="sd-course-image"
            />

            <div className="sd-course-content">

              <h3>{course.title}</h3>

              <p>{course.description}</p>

              <div className="sd-course-meta">

                <span>⏱ {course.duration}</span>

                <span>₹{course.price}</span>

              </div>

              <button
                className="sd-primary-btn"
              >
                Continue Learning
              </button>

            </div>

          </div>

        ))}

      </div>

    )}

  </section>
);

  const renderProfile = () => (
    <section className="sd-panel">
      <div className="sd-panel-head">
        <h3>Profile</h3>
        <span>Your learner details</span>
      </div>
      <div className="sd-profile-card">
        <div className="sd-profile-identity">
          {hasPhoto ? (
            <img
              src={profilePhoto}
              alt="profile"
              className="sd-profile-image"
              referrerPolicy="no-referrer"
            />
          ) : (
            <div className="sd-profile-fallback">{initials || "ST"}</div>
          )}
          <div>
            <strong>{displayName}</strong>
            <p>{user?.email}</p>
            <span>{userData?.userID || "Learner account"}</span>
          </div>
        </div>

        <div className="sd-profile-grid">
          <div className="sd-profile-field">
            <label>Full name</label>
            <input defaultValue={displayName} className="sd-input" />
          </div>
          <div className="sd-profile-field">
            <label>Email address</label>
            <input defaultValue={user?.email || ""} className="sd-input" disabled />
          </div>
          <div className="sd-profile-field">
            <label>Phone number</label>
            <input defaultValue={userData?.phone || ""} className="sd-input" placeholder="+91 XXXXX XXXXX" />
          </div>
          <div className="sd-profile-field">
            <label>Learning goal</label>
            <input className="sd-input" placeholder="Frontend developer role" />
          </div>
        </div>
      </div>
    </section>
  );

  const renderContent = () => {
  switch (active) {
    case "home":
      return renderOverview();

    case "courses":
      return renderCourses();

    case "live":
      return renderPlaceholder(
        "Live Classes",
        "Your scheduled mentor-led live classes will appear here."
      );

    case "recordings":
      return renderPlaceholder(
        "Recordings",
        "Recorded mentor sessions will appear here for revision."
      );

    case "modules":
      return renderPlaceholder(
        "Modules",
        "Module timelines, lessons, and learning paths will appear here."
      );

    case "assignments":
      return renderPlaceholder(
        "Assignments",
        "Track pending submissions, grades, and mentor feedback."
      );

    case "projects":
      return renderPlaceholder(
        "Projects",
        "Your course projects and portfolio progress will appear here."
      );

    case "results":
      return renderPlaceholder(
        "Results",
        "Assessment scores and performance insights will be shown here."
      );

    case "career":
      return renderPlaceholder(
        "Job Assistance",
        "Resume reviews, LinkedIn support, mock interviews, portfolio reviews, and career guidance will appear here."
      );

    case "profile":
      return renderProfile();

    default:
      return renderOverview();
  }
};

  return (
    <div className="sd-layout">
      <aside className="sd-sidebar">
        <div>
          <div className="sd-brand">
            <span className="sd-brand-main">CODE</span>
            <span className="sd-brand-accent">ION</span>
          </div>
          <p className="sd-role-tag">Student Portal</p>
        </div>

        <nav className="sd-nav">
          {studentMenuItems.map((item) => (
            <button
              key={item.key}
              onClick={() => setActive(item.key)}
              className={`sd-nav-btn ${active === item.key ? "sd-nav-active" : ""}`}
            >
              <span className="sd-nav-icon">{item.icon}</span>
              <span>{item.label}</span>
            </button>
          ))}
        </nav>

        <div className="sd-sidebar-footer">
          <span>{userData?.userID || user?.email}</span>
          <button className="sd-logout-btn" onClick={logout}>
            Logout
          </button>
        </div>
      </aside>

      <main className="sd-main">
        <header className="sd-topbar">
          <div>
            <span className="sd-page-kicker">Dashboard</span>
            <h2>{pageTitle}</h2>
          </div>

          <div className="sd-user-menu" onClick={() => setOpenDropdown(!openDropdown)}>
            {hasPhoto ? (
              <img
                src={profilePhoto}
                alt="profile"
                className="sd-user-photo"
                referrerPolicy="no-referrer"
              />
            ) : (
              <div className="sd-user-fallback">{initials || "ST"}</div>
            )}
            <div className="sd-user-meta">
              <strong>{displayName}</strong>
              <span>{user?.email}</span>
            </div>
            <div className="sd-user-status"></div>

            {openDropdown && (
              <div className="sd-dropdown">
                <button onClick={() => setActive("profile")}>Profile</button>
                <button onClick={logout}>Logout</button>
              </div>
            )}
          </div>
        </header>

        <div className="sd-body">{renderContent()}</div>
      </main>
    </div>
  );
}

function Dashboard() {
  const { role, userData, loading } = useAuth();

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          background: "#0b1020",
          color: "white",
          fontSize: "1.2rem",
        }}
      >
        Loading...
      </div>
    );
  }

  if (role === ROLES.ADMIN) {
    return <AdminDashboard userData={userData} />;
  }

  if (role === ROLES.INSTRUCTOR) {
    return <InstructorDashboard userData={userData} />;
  }

  return <StudentDashboard userData={userData} />;
}

export default Dashboard;
